import React from 'react';
import Icon from '../../../components/AppIcon';


const PreviewStep = ({ formData }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '미정';
    const date = new Date(dateString);
    return date?.toLocaleDateString('ko-KR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      weekday: 'long'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  const formatCurrency = (amount) => {
    return `₩${parseInt(amount || 0)?.toLocaleString()}`;
  };

  const getMatchFormatLabel = (format) => {
    const labels = {
      'roundrobin': '라운드 로빈',
      'tournament': '토너먼트',
      'random': '랜덤 매칭',
      'manual': '수동 배정'
    };
    return labels?.[format] || '미설정';
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-accent/10 border border-accent/20 rounded-md p-4 md:p-6">
        <div className="flex items-start gap-3">
          <Icon name="Eye" size={20} className="text-accent mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-foreground mb-2">
              미리보기
            </h3>
            <p className="text-sm caption text-muted-foreground">
              생성될 정보들을 확인하세요. 수정이 필요한 경우 이전 단계로 돌아갈 수 있습니다.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl p-6 md:p-8 space-y-6">
        {(() => {
          const coverDraftId = String(formData?.coverDraftId || '').trim();
          const drafts = Array.isArray(formData?.imageDrafts) ? formData.imageDrafts : [];
          const draftCover = coverDraftId ? drafts.find((d) => String(d?.id || '').trim() === coverDraftId) : null;
          const coverUrl = String(formData?.coverImageUrl || '').trim() || String(draftCover?.previewUrl || '').trim();
          if (!coverUrl) return null;
          return (
          <div className="w-full overflow-hidden rounded-xl border border-border bg-muted">
            <div className="aspect-[16/9]">
              <img
                src={coverUrl}
                alt="cover"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          );
        })()}

        {(() => {
          const urls = Array.isArray(formData?.imageUrls) ? formData.imageUrls : [];
          const drafts = Array.isArray(formData?.imageDrafts) ? formData.imageDrafts : [];
          const draftUrls = drafts.map((d) => d?.previewUrl).filter(Boolean);
          const all = [...urls, ...draftUrls].filter(Boolean);
          if (all.length <= 1) return null;
          return (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {all.slice(0, 12).map((u) => (
              <div key={u} className="aspect-square rounded-md overflow-hidden border border-border bg-muted">
                <img src={u} alt="session" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
          );
        })()}

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
              {formData?.title || '제목 없음'}
            </h2>
            <p className="text-sm caption text-muted-foreground">
              {formData?.description || '설명이 없습니다.'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div className="flex items-start gap-3 p-4 bg-muted rounded-md">
            <Icon name="Calendar" size={20} className="text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm caption text-muted-foreground mb-1">날짜</div>
              <div className="font-medium text-foreground">{formatDate(formData?.date)}</div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-muted rounded-md">
            <Icon name="Clock" size={20} className="text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm caption text-muted-foreground mb-1">시간</div>
              <div className="font-medium text-foreground">
                {formatTime(formData?.startTime)} - {formatTime(formData?.endTime)}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-muted rounded-md">
            <Icon name="MapPin" size={20} className="text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm caption text-muted-foreground mb-1">장소</div>
              <div className="font-medium text-foreground">{formData?.venue || '미정'}</div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-muted rounded-md">
            <Icon name="Users" size={20} className="text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm caption text-muted-foreground mb-1">정원</div>
              <div className="font-medium text-foreground">
                최대 {formData?.maxParticipants || 0}명
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl p-6 md:p-8 space-y-4">
        <h3 className="text-xl font-heading font-semibold text-foreground flex items-center gap-2">
          <Icon name="DollarSign" size={24} className="text-primary" />
          가격 정보
        </h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-border">
            <span className="text-sm caption text-muted-foreground">가격</span>
            <span className="font-medium text-foreground data-text">{formatCurrency(formData?.memberPrice)}</span>
          </div>

          <div className="flex items-center justify-between py-2">
            <span className="text-sm caption text-muted-foreground">결제 계좌</span>
            <span className="font-medium text-foreground data-text">
              {formData?.bankName} {formData?.accountNumber}
            </span>
          </div>
        </div>
      </div>
      <div className="bg-warning/10 border border-warning/20 rounded-md p-4 md:p-6">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-heading font-medium text-foreground mb-3">
              확인 사항
            </h4>
            <ul className="space-y-1 text-sm caption text-muted-foreground">
              <li>• 생성 후에는 일부 정보만 수정 가능합니다</li>
              <li>• 참가자가 등록한 후에는 가격 정보를 변경할 수 없습니다</li>
              <li>• 공유 링크는 생성 즉시 활성화됩니다</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewStep;